import { Box } from '@mui/material';
import { Container, Heading, Section, Text } from '../components/atomic';
import SecureEmail from '../components/SecureEmail';
import { LegalPageLayout } from '../layouts';

/**
 * Accessibility page component for RSM Collective
 *
 * Comprehensive accessibility statement explaining RSM Collective's
 * commitment to web accessibility and compliance with accessibility standards.
 *
 * @returns {JSX.Element} Accessibility page component
 */
function Accessibility() {
  return (
    <LegalPageLayout>
      <Section
        sx={{
          backgroundColor: 'var(--color-background-light)',
          py: 0,
          position: 'relative',
        }}
      >
        {/* Green Bar Header Treatment */}
        <Box
          sx={{
            borderTop: '4px solid var(--color-primary)',
            borderBottom: '4px solid var(--color-primary)',
            backgroundColor:
              'color-mix(in srgb, var(--color-primary) 5%, transparent)',
            py: 4,
            mb: 6,
          }}
        >
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center' }}>
              <Heading
                variant="h1"
                sx={{
                  color: 'text.primary',
                  fontWeight: 600,
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  fontFamily: '"Playfair Display", serif',
                }}
              >
                Accessibility Statement
              </Heading>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ pb: 8 }}>
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {/* Introduction */}
            <Box sx={{ mb: 6 }}>
              <Text sx={{ fontSize: '1.125rem', lineHeight: 1.7, mb: 3 }}>
                RSM Collective is committed to ensuring digital accessibility
                for people with disabilities. We are continually improving the
                user experience for everyone and applying the relevant
                accessibility standards to ensure we provide equal access to all
                of our users.
              </Text>
            </Box>

            {/* Our Commitment */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Our Commitment
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We believe that everyone should have access to information and
                functionality on our website, regardless of their abilities or
                disabilities. We are committed to providing an inclusive digital
                experience that enables all users to successfully access our
                content and services.
              </Text>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                This commitment reflects our core values of diversity,
                inclusion, and innovation in everything we do, from our AWS
                consulting services to our AI-powered technology solutions.
              </Text>
            </Box>

            {/* Standards and Guidelines */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Accessibility Standards
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                Our website aims to conform to the Web Content Accessibility
                Guidelines (WCAG) 2.1, Level AA. These guidelines explain how to
                make web content more accessible for people with disabilities
                and user-friendly for everyone.
              </Text>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                The guidelines have three levels of conformance: Level A, Level
                AA, and Level AAA. We strive to achieve Level AA conformance,
                which addresses the major barriers for disabled users.
              </Text>
            </Box>

            {/* Accessibility Features */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Accessibility Features
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We have implemented the following accessibility features
                throughout our website:
              </Text>

              <Heading
                variant="h3"
                sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
              >
                Keyboard Navigation
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                All interactive elements can be accessed and operated using
                keyboard navigation, including tab and arrow keys.
              </Text>

              <Heading
                variant="h3"
                sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
              >
                Screen Reader Compatibility
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                Our website is compatible with popular screen readers including
                JAWS, NVDA, and VoiceOver, with proper heading structure and alt
                text for images.
              </Text>

              <Heading
                variant="h3"
                sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
              >
                Color and Contrast
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We use high contrast ratios between text and background colors
                to ensure readability for users with visual impairments or color
                blindness.
              </Text>

              <Heading
                variant="h3"
                sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
              >
                Responsive Design
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                Our website is fully responsive and works across different
                devices and screen sizes, supporting zoom up to 200% without
                loss of functionality.
              </Text>

              <Heading
                variant="h3"
                sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
              >
                Clear Navigation
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We provide clear, consistent navigation with descriptive links
                and skip navigation options for screen reader users.
              </Text>
            </Box>

            {/* Known Limitations */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Known Limitations
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                Despite our efforts to ensure accessibility, there may be some
                limitations. We are actively working to address these issues:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Some third-party embedded content may not be fully accessible
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Certain complex interactive elements are being optimized for
                  better accessibility
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Some PDF documents are in the process of being made more
                  accessible
                </Text>
              </Box>
            </Box>

            {/* Assistive Technologies */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Supported Assistive Technologies
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                Our website is designed to be compatible with the following
                assistive technologies:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Screen readers (JAWS, NVDA, VoiceOver, TalkBack)
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Voice recognition software
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Switch navigation devices
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Alternative keyboards and pointing devices
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Browser zoom functionality and text enlargement software
                </Text>
              </Box>
            </Box>

            {/* Testing and Compliance */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Testing and Compliance
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We regularly test our website for accessibility using:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Automated accessibility testing tools
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Manual testing with keyboard navigation
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Screen reader testing
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  User testing with people with disabilities
                </Text>
              </Box>
            </Box>

            {/* Ongoing Improvements */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Ongoing Improvements
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                Accessibility is an ongoing process, and we are committed to
                continuously improving our website. Our efforts include:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Regular accessibility audits and testing
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Staff training on accessibility best practices
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  User feedback integration
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Updates based on evolving accessibility standards
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Third-party accessibility consultations
                </Text>
              </Box>
            </Box>

            {/* Feedback and Contact */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Feedback and Assistance
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We welcome your feedback on the accessibility of our website. If
                you encounter accessibility barriers or need assistance
                accessing any content, please contact us:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Email:{' '}
                  <SecureEmail
                    emailKey="support"
                    showAsLink={false}
                    component="span"
                  />
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Mail: 401 West A St. Ste. 200, San Diego, CA 92101
                </Text>
              </Box>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We aim to respond to accessibility feedback within 2 business
                days and will work with you to provide the information, item, or
                service you seek through an accessible communication method or
                alternative format.
              </Text>
            </Box>

            {/* Alternative Formats */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Alternative Formats
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                If you need information from our website in a different format,
                we can provide:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Large print documents
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Audio recordings
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Accessible PDF formats
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Plain text versions
                </Text>
              </Box>
            </Box>

            {/* Legal Information */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Legal Information
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                This accessibility statement was last updated on{' '}
                {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
                . We review and update this statement regularly to ensure it
                reflects our current accessibility practices.
              </Text>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                RSM Collective is committed to compliance with applicable
                accessibility laws and regulations, including the Americans with
                Disabilities Act (ADA) and Section 508 of the Rehabilitation
                Act.
              </Text>
            </Box>
          </Box>
        </Container>
      </Section>
    </LegalPageLayout>
  );
}

export default Accessibility;
